import React from "react";
import { qformat, DATE_FORMATS, formatRelativeVerbose } from "views_shared/app/javascript/utils/dateFormatters";

/**
 * DiagnosticSummary - Quick overview of notification health
 *
 * Shows key metrics at a glance:
 * - Eligibility status (in good standing)
 * - Email activity (7d, 30d counts)
 * - Last email sent
 * - Daily limit status
 */
function DiagnosticSummary({ diagnostics, onViewFullReport }) {
  if (!diagnostics) {
    return (
      <div className="card mb-4">
        <div className="card-body">
          <div className="text-muted text-center py-3">
            <i className="fa fa-info-circle me-2" />
            No notification diagnostics available for this user.
          </div>
        </div>
      </div>
    );
  }

  const isEligible = diagnostics.in_good_standing;
  const hasIssues = !isEligible || diagnostics.at_daily_limit;

  return (
    <div className={`card mb-4 ${hasIssues ? "border-warning" : "border-success"}`} style={{ borderLeftWidth: "4px" }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            <i
              className={`fa ${isEligible ? "fa-check-circle text-success" : "fa-exclamation-circle text-danger"} fs-4 me-3`}
            />
            <div>
              <h5 className="mb-0">{isEligible ? "Eligible for Notifications" : "Not Eligible for Notifications"}</h5>
              {!isEligible && diagnostics.ineligibility_reasons?.length > 0 && (
                <small className="text-muted">
                  {diagnostics.ineligibility_reasons.length} issue
                  {diagnostics.ineligibility_reasons.length > 1 ? "s" : ""} found
                </small>
              )}
            </div>
          </div>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onViewFullReport}>
            <i className="fa fa-file-text me-1" />
            Full Report
          </button>
        </div>

        <div className="row g-3">
          <div className="col-md-2">
            <div className="text-muted small text-uppercase">Notification Frequency</div>
            <div className="fs-5 fw-semibold">
              {diagnostics.spam_tolerance ? `${diagnostics.spam_tolerance} / 5` : "Default"}
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-muted small text-uppercase">Emails (7d)</div>
            <div className="fs-4 fw-semibold">{diagnostics.emails_7d || 0}</div>
          </div>
          <div className="col-md-2">
            <div className="text-muted small text-uppercase">Emails (30d)</div>
            <div className="fs-4 fw-semibold">{diagnostics.emails_30d || 0}</div>
          </div>
          <div className="col-md-3">
            <div className="text-muted small text-uppercase">Last Email</div>
            <div className="fs-5 fw-semibold">{formatRelativeVerbose(diagnostics.last_email_at)}</div>
          </div>
          <div className="col-md-3">
            <div className="text-muted small text-uppercase">Daily Limit</div>
            <div className="fs-5 fw-semibold">
              {diagnostics.at_daily_limit ? (
                <span className="text-warning">
                  <i className="fa fa-exclamation-triangle me-1" />
                  At Limit
                </span>
              ) : (
                <span className="text-success">
                  {diagnostics.emails_sent_today || 0}
                  {diagnostics.daily_engagement_email_limit ? ` / ${diagnostics.daily_engagement_email_limit}` : ""}
                </span>
              )}
            </div>
          </div>
        </div>

        {diagnostics.refreshed_at && (
          <div className="text-muted small mt-3 pt-2 border-top">
            <i className="fa fa-clock-o me-1" />
            Data refreshed: {formatRelativeVerbose(diagnostics.refreshed_at)}
            <span className="ms-1">({qformat(diagnostics.refreshed_at, DATE_FORMATS.dateTime)})</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default DiagnosticSummary;
